import { useEffect, useMemo, useRef, useState } from "react";
import { getNearbyStops } from "../utils/bods";
import { countRouteVehicles, getLiveEstimateForService, normaliseRoute } from "../utils/liveEta";

const ROUTE_COLOURS = {
  "1": "#003087", "1A": "#003087", "1S": "#003087", "2": "#003087", "5": "#003087", "6": "#003087",
  "8": "#003087", "9": "#003087", "14": "#003087", "20": "#003087", "20A": "#003087", "20B": "#003087",
  "21": "#003087", "21A": "#003087", "27": "#003087", "28": "#003087", "30": "#003087", "31": "#003087",
  "33": "#185FA5", "34": "#185FA5", "34A": "#185FA5", "35": "#185FA5", "35A": "#185FA5",
  "41": "#003087", "42": "#003087", "42A": "#003087", "42B": "#003087", "42C": "#003087", "42D": "#003087",
  "43": "#185FA5", "43A": "#185FA5", "44": "#185FA5", "48": "#185FA5", "50": "#1D9E75", "51": "#1D9E75", "61": "#1D9E75",
  "70": "#2c6e16", "70A": "#2c6e16", "70B": "#2c6e16", "70S": "#2c6e16",
};

const TYPE_CLASS = {
  Normal: "type-normal",
  School: "type-school",
  "Park & Ride": "type-parkride",
  Circular: "type-circular",
  Special: "type-special",
};

const RADIUS_OPTIONS = [0.25, 0.4, 0.8, 1.2, 1.6];
const MAX_STOPS_BY_RADIUS = { 0.25: 8, 0.4: 10, 0.8: 14, 1.2: 18, 1.6: 24 };

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
let leafletReady = false;
let leafletCallbacks = [];

function loadLeaflet(cb) {
  if (window.L || leafletReady) { leafletReady = true; return cb(); }
  leafletCallbacks.push(cb);
  if (leafletCallbacks.length > 1) return;

  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }

  const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
  const finish = () => {
    leafletReady = true;
    const callbacks = leafletCallbacks;
    leafletCallbacks = [];
    callbacks.forEach((fn) => fn());
  };
  if (existing) {
    existing.addEventListener("load", finish, { once: true });
    return;
  }
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = finish;
  document.head.appendChild(script);
}

function createStopIcon(isMain = false) {
  const L = window.L;
  return L.divIcon({
    className: "",
    html: `<div class="leaflet-stop-pin ${isMain ? "main" : "nearby"}">🚏</div>`,
    iconSize: isMain ? [30, 30] : [24, 24],
    iconAnchor: isMain ? [15, 28] : [12, 22],
  });
}

function createBusIcon(route, bearing = 0, expected = false) {
  const L = window.L;
  const safeRoute = String(route || "bus").replace(/</g, "");
  return L.divIcon({
    className: "",
    html: `<div class="leaflet-bus-pin ${expected ? "expected" : "seen"}" style="transform: rotate(${Number(bearing) || 0}deg)"><span style="transform: rotate(-${Number(bearing) || 0}deg)">${safeRoute}</span></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function MapTilePanel({ row, detailed = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  const estimate = row.liveEstimate || null;
  const stopList = (row.stops?.length ? row.stops : [row.stop]).filter(Boolean);
  const hasBus = Number.isFinite(estimate?.vehicleLat) && Number.isFinite(estimate?.vehicleLng);
  const safeExpected = Boolean(getExpectedIfSafe(row));
  const routeLabel = (row.routes || [row.route]).join("/");

  useEffect(() => {
    let destroyed = false;
    loadLeaflet(() => {
      if (destroyed || !containerRef.current) return;
      const L = window.L;
      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, {
          zoomControl: detailed,
          attributionControl: detailed,
          dragging: detailed,
          scrollWheelZoom: false,
          doubleClickZoom: detailed,
          boxZoom: detailed,
          keyboard: detailed,
          tap: detailed,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: detailed ? '© <a href="https://openstreetmap.org">OpenStreetMap</a>' : "",
          maxZoom: 19,
        }).addTo(mapRef.current);
        layerRef.current = L.layerGroup().addTo(mapRef.current);
      }

      layerRef.current.clearLayers();
      const bounds = [];
      stopList.slice(0, detailed ? 12 : 4).forEach((stop, index) => {
        if (!Number.isFinite(stop.lat) || !Number.isFinite(stop.lng)) return;
        bounds.push([stop.lat, stop.lng]);
        L.marker([stop.lat, stop.lng], { icon: createStopIcon(index === 0) })
          .bindPopup(`<strong>${stop.name}</strong><br/><small>${formatDistance(stop)}</small>`)
          .addTo(layerRef.current);
      });

      if (hasBus) {
        bounds.push([estimate.vehicleLat, estimate.vehicleLng]);
        L.marker([estimate.vehicleLat, estimate.vehicleLng], { icon: createBusIcon(routeLabel, estimate.bearing, safeExpected) })
          .bindPopup(`<strong>Tracked bus ${routeLabel}</strong><br/><small>${estimate.distance || "Live position"}${safeExpected ? " · expected" : " · not confirmed next"}</small>`)
          .addTo(layerRef.current);
        if (stopList[0]?.lat && stopList[0]?.lng) {
          L.polyline([[estimate.vehicleLat, estimate.vehicleLng], [stopList[0].lat, stopList[0].lng]], {
            color: safeExpected ? "#16d37f" : "#f59e0b",
            weight: detailed ? 4 : 3,
            opacity: 0.85,
            dashArray: safeExpected ? null : "6 6",
          }).addTo(layerRef.current);
        }
      }

      if (bounds.length >= 2) {
        mapRef.current.fitBounds(bounds, { padding: detailed ? [42, 42] : [18, 18], maxZoom: detailed ? 17 : 16 });
      } else if (bounds.length === 1) {
        mapRef.current.setView(bounds[0], detailed ? 17 : 16);
      } else {
        mapRef.current.setView([50.3714, -4.1425], 13);
      }
      setTimeout(() => mapRef.current?.invalidateSize(), 80);
    });
    return () => { destroyed = true; };
  }, [rowIdentity(row), detailed, hasBus, safeExpected, routeLabel]);

  return <div ref={containerRef} className={detailed ? "real-service-map real-service-map--large" : "real-service-map real-service-map--mini"} />;
}

const COMPATIBLE_DIRECTIONS = {
  E: new Set(["E", "NE", "SE"]), W: new Set(["W", "NW", "SW"]),
  N: new Set(["N", "NE", "NW"]), S: new Set(["S", "SE", "SW"]),
  NE: new Set(["NE", "N", "E"]), NW: new Set(["NW", "N", "W"]),
  SE: new Set(["SE", "S", "E"]), SW: new Set(["SW", "S", "W"]),
};

function minutesUntilEndOfToday() {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return Math.max(0, Math.floor((end.getTime() - now.getTime()) / 60000));
}

function formatNextLabel(item) {
  if (!item || typeof item.minutes !== "number") return "Scheduled";
  const mins = item.minutes;
  if (mins <= 1) return "Due";
  if (mins < 60) return `${mins} mins`;
  if (mins < 120) return mins === 60 ? "1 hr" : `1 hr ${mins - 60} min`;
  const endOfToday = minutesUntilEndOfToday();
  if (mins <= endOfToday) return item.time || "Later today";
  if (mins <= endOfToday + 24 * 60) return `Tomorrow ${item.time}`;
  return item.time || "Later";
}

function formatDistance(stop) {
  if (stop?.distanceKm === undefined) return "Selected stop";
  return stop.distanceKm < 1 ? `${Math.round(stop.distanceKm * 1000)}m away` : `${stop.distanceKm.toFixed(1)}km away`;
}

function formatClockList(times = []) {
  return times.slice(0, 3).map((item, index) => {
    if (index === 0 && item.minutes > minutesUntilEndOfToday()) return `Tomorrow ${item.time}`;
    return item.time;
  }).join(" · ");
}

function getDirection(stopName = "") {
  const match = String(stopName).match(/\b(N|S|E|W|NE|NW|SE|SW)-bound\b/i);
  return match ? match[1].toUpperCase() : "";
}

function isSameSide(anchorStop, stop) {
  if (!anchorStop || !stop || anchorStop.id === stop.id) return true;
  const anchorDir = getDirection(anchorStop.name);
  const stopDir = getDirection(stop.name);
  if (!anchorDir || !stopDir) return true;
  return COMPATIBLE_DIRECTIONS[anchorDir]?.has(stopDir) ?? anchorDir === stopDir;
}

function sortRoutes(routes) {
  return Array.from(new Set(routes.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function RouteBadge({ route, routes }) {
  const values = routes?.length ? routes : [route];
  return (
    <div className="route-stack" aria-label={`Service ${values.join(" or ")}`}>
      {values.slice(0, 4).map((value) => (
        <div key={value} className="route-badge board-route-badge" style={{ background: ROUTE_COLOURS[value] || "#003087" }}>{value}</div>
      ))}
      {values.length > 4 && <span className="route-more">+{values.length - 4}</span>}
    </div>
  );
}

function TypePill({ type }) {
  const label = type || "Normal";
  return <span className={`type-pill ${TYPE_CLASS[label] || "type-normal"}`}>{label}</span>;
}

async function fetchStopServices(atco) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 65000);
  try {
    // Main board now uses GTFS stop_times. This is stricter than the old
    // TransXChange scanner because every departure is tied to an exact stop_id.
    const res = await fetch(`/api/gtfs-board?atco=${encodeURIComponent(atco)}`, { signal: controller.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.error || `GTFS board failed: ${res.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

function serviceToRow(service, stop) {
  const route = service.route;
  const destination = service.destination || "See timetable";
  const nextTimes = service.nextTimes || service.scheduledTimes || [];
  return {
    key: `${stop.id}-${route}-${destination}-${service.operatorName || service.operatorRef || ""}`,
    stop,
    route,
    routes: [route],
    destination,
    operator: service.operatorName || service.operatorRef || "Operator to confirm",
    serviceType: service.serviceType || "Normal",
    nextTimes,
    sortMinutes: nextTimes?.[0]?.minutes ?? 999999,
  };
}

function getExpectedIfSafe(row) {
  const first = row.nextTimes?.[0] || null;
  const estimate = row.liveEstimate || null;
  if (!first || !estimate) return null;
  if (!Number.isFinite(first.minutes) || !Number.isFinite(estimate.minutes)) return null;
  const closeToSchedule = Math.abs(estimate.minutes - first.minutes) <= 4;
  const routeMatches = (row.routes || [row.route]).some((route) => normaliseRoute(route) === normaliseRoute(estimate.routeMatched || row.route));
  const reliableConfidence = estimate.confidence !== "low";
  return closeToSchedule && routeMatches && reliableConfidence ? estimate : null;
}

function getBoardDue(row) {
  const first = row.nextTimes?.[0] || null;
  const expected = getExpectedIfSafe(row);
  if (expected) {
    return { label: expected.minutes <= 1 ? "Due" : `${expected.minutes} mins`, qualifier: "Expected", detail: first?.time ? `Scheduled ${first.time}` : "Live tracking agrees", source: "expected" };
  }
  if (first) return { label: formatNextLabel(first), qualifier: "Due", detail: formatClockList(row.nextTimes), source: "scheduled" };
  return null;
}

function BoardTimeCell({ row }) {
  const due = getBoardDue(row);
  if (!due) return <div className="simple-due simple-due--none"><strong>—</strong><span>No due time</span></div>;
  return (
    <div className={`simple-due simple-due--${due.source}`}>
      <strong>{due.label}</strong>
      <span>{due.qualifier}</span>
      <small>{due.detail}</small>
    </div>
  );
}

function SimpleBoardDue({ row }) {
  const due = getBoardDue(row);
  if (!due) return <span className="tv-due tv-due--none">—</span>;
  const label = due.label === "Due" ? "Due" : due.label;
  return <span className={`tv-due tv-due--${due.source}`}>{due.source === "expected" ? `Exp ${label}` : label}</span>;
}

function SimpleTvBoard({ rows, selectedStopOnly, loading, progress, fullScreen = false }) {
  const time = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const maxRows = fullScreen ? 18 : 12;
  const densityClass = rows.length >= 10 ? "tv-board-card--dense" : rows.length <= 4 ? "tv-board-card--roomy" : "";
  return (
    <section className={`tv-board-card ${densityClass} ${fullScreen ? "tv-board-card--fullscreen" : ""}`} aria-label="Simple bus departure board">
      <div className="tv-board-top">
        <div className="tv-board-brand">Plymouth Bus Departures</div>
        <div className="tv-board-clock">{time}</div>
      </div>
      <div className={`tv-board-head ${selectedStopOnly ? "" : "tv-board-head--area"}`}>
        <span>Service</span>
        <span>Destination</span>
        <span>Company</span>
        <span>Due</span>
        {!selectedStopOnly && <span>Stop</span>}
      </div>
      {loading && !rows.length && (
        <div className="tv-board-empty">Checking {progress.done}/{progress.total} stops…</div>
      )}
      {!loading && !rows.length && (
        <div className="tv-board-empty">No timed departures found for this stop.</div>
      )}
      <div className="tv-board-list">
        {rows.slice(0, maxRows).map((row) => {
          const key = `simple-${row.key}-${(row.routes || []).join("-")}-${row.destination}-${row.nextTimes?.[0]?.time || "x"}`;
          return (
            <div key={key} className={`tv-board-row ${selectedStopOnly ? "" : "tv-board-row--area"}`}>
              <div className="tv-service">{(row.routes || [row.route]).join("/")}</div>
              <div className="tv-destination">
                <strong>{row.destination}</strong>
              </div>
              <div className="tv-company-cell">{row.operator || "Operator"}</div>
              <div className="tv-due-cell"><SimpleBoardDue row={row} /></div>
              {!selectedStopOnly && <div className="tv-stop">{row.stop?.name?.replace(/\s+[A-Z]{1,2}-bound$/i, "") || "Nearby stop"}</div>}
            </div>
          );
        })}
      </div>
      <div className="tv-board-footer">
        {selectedStopOnly ? "Selected stop only" : "Local area view"} · Due times from GTFS timetable
      </div>
    </section>
  );
}

function addRowLive(row, vehicles) {
  const serviceForLive = { route: row.route, dest: row.destination, destination: row.destination };
  return {
    ...row,
    liveEstimate: getLiveEstimateForService(serviceForLive, row.stop, vehicles),
    liveRouteCount: countRouteVehicles(serviceForLive, vehicles),
  };
}

function makeBoardKey(row, selectedStopOnly) {
  const destinationKey = String(row.destination || "").toUpperCase().replace(/\s+/g, " ").trim();
  const operatorKey = String(row.operator || "").toUpperCase().replace(/\s+/g, " ").trim();
  // Selected-stop mode should behave like a normal bus stop departure board.
  // Local-area mode intentionally groups the same service/time across nearby stops,
  // otherwise the same route appears six times down one road and confuses users.
  const stopKey = selectedStopOnly ? row.stop.id : "LOCAL_AREA";
  const routeKey = String(row.route || "").toUpperCase().replace(/\s+/g, " ").trim();
  return [stopKey, routeKey, destinationKey, operatorKey].join("|");
}

function groupBoardRows(rows, selectedStopOnly) {
  const map = new Map();
  for (const row of rows) {
    const key = makeBoardKey(row, selectedStopOnly);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...row, routes: [row.route], stops: [row.stop] });
      continue;
    }
    existing.routes = sortRoutes([...(existing.routes || [existing.route]), row.route]);
    existing.route = existing.routes.join("/");
    existing.stops = [...(existing.stops || [existing.stop]), row.stop]
      .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
      .filter((stop, index, arr) => arr.findIndex((s) => s.id === stop.id) === index);
    existing.stop = existing.stops[0];
    const mergedTimes = [...(existing.nextTimes || []), ...(row.nextTimes || [])]
      .filter((item) => item && Number.isFinite(item.minutes))
      .sort((a, b) => a.minutes - b.minutes)
      .filter((item, index, arr) => arr.findIndex((other) => other.time === item.time && other.minutes === item.minutes) === index)
      .slice(0, 3);
    existing.nextTimes = mergedTimes;
    existing.scheduledTimes = mergedTimes;
    existing.serviceType = existing.serviceType === "School" || row.serviceType === "School" ? "School" : existing.serviceType;
    if (!existing.liveEstimate && row.liveEstimate) existing.liveEstimate = row.liveEstimate;
    if ((row.liveRouteCount || 0) > (existing.liveRouteCount || 0)) existing.liveRouteCount = row.liveRouteCount;
    map.set(key, existing);
  }
  return Array.from(map.values()).map((row) => ({ ...row, routes: sortRoutes(row.routes || [row.route]) }));
}

function StopCell({ row, selectedStopOnly }) {
  if (selectedStopOnly || !row.stops || row.stops.length <= 1) {
    return <div className="train-stop-cell"><strong>{row.stop.name}</strong><small>{formatDistance(row.stop)}</small></div>;
  }
  return (
    <div className="train-stop-cell">
      <strong>{row.stops.length} nearby stops</strong>
      <small>Nearest: {row.stops[0].name} · {formatDistance(row.stops[0])}</small>
      <div className="stop-chip-list">
        {row.stops.slice(0, 4).map((stop) => <span key={stop.id}>{stop.name.replace(/\s+[A-Z]{1,2}-bound$/i, "")}</span>)}
        {row.stops.length > 4 && <span>+{row.stops.length - 4} more</span>}
      </div>
    </div>
  );
}

function getMapPoints(row) {
  const estimate = row.liveEstimate || {};
  const stopList = row.stops?.length ? row.stops : [row.stop].filter(Boolean);
  const points = [];
  stopList.forEach((stop) => {
    if (Number.isFinite(stop?.lat) && Number.isFinite(stop?.lng)) points.push({ type: "stop", lat: stop.lat, lng: stop.lng, label: stop.name, stop });
  });
  if (Number.isFinite(estimate?.vehicleLat) && Number.isFinite(estimate?.vehicleLng)) {
    points.push({ type: "bus", lat: estimate.vehicleLat, lng: estimate.vehicleLng, label: `Route ${(row.routes || [row.route]).join("/")}` });
  }
  return points;
}

function buildProjection(points, width = 140, height = 76, pad = 12) {
  const safe = points.length ? points : [{ lat: 50.3714, lng: -4.1425 }];
  const lats = safe.map((p) => p.lat);
  const lngs = safe.map((p) => p.lng);
  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  const lngMin = Math.min(...lngs);
  const lngMax = Math.max(...lngs);
  const latSpan = Math.max(latMax - latMin, 0.0018);
  const lngSpan = Math.max(lngMax - lngMin, 0.0018);
  return (lat, lng) => {
    const x = pad + ((lng - (lngMin - lngSpan * 0.18)) / (lngSpan * 1.36)) * (width - pad * 2);
    const y = height - pad - ((lat - (latMin - latSpan * 0.18)) / (latSpan * 1.36)) * (height - pad * 2);
    return { x: Math.max(pad, Math.min(width - pad, x)), y: Math.max(pad, Math.min(height - pad, y)) };
  };
}

function rowIdentity(row) {
  return `${(row.routes || [row.route]).join("-")}-${row.destination}-${row.stop?.id}-${row.nextTimes?.[0]?.time || "notime"}`;
}

function MiniBusMap({ row, onOpen }) {
  const estimate = row.liveEstimate;
  const safeExpected = Boolean(getExpectedIfSafe(row));

  return (
    <div className="mini-bus-map mini-bus-map--leaflet">
      <MapTilePanel row={row} detailed={false} />
      <div className="mini-map-caption mini-map-caption--leaflet">
        <strong>{estimate?.vehicleLat ? (safeExpected ? "Expected bus" : "Tracked bus") : row.liveRouteCount > 0 ? "Live buses tracked" : "Timetable only"}</strong>
        <span>
          {estimate?.vehicleLat
            ? `${estimate.distance || "Live position"}${safeExpected ? "" : " · not confirmed next"}`
            : row.liveRouteCount > 0
            ? `${row.liveRouteCount} on route · ETA unavailable`
            : "No matching live bus yet"}
        </span>
      </div>
      <button type="button" className="mini-map-open" onClick={onOpen}>Open larger map</button>
    </div>
  );
}

function DetailedServiceMap({ row, onClose }) {
  const estimate = row.liveEstimate || null;
  const due = getBoardDue(row);
  const safeExpected = Boolean(getExpectedIfSafe(row));
  const stop = row.stop;
  const osmUrl = estimate?.vehicleLat && estimate?.vehicleLng && stop?.lat && stop?.lng
    ? `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${estimate.vehicleLat}%2C${estimate.vehicleLng}%3B${stop.lat}%2C${stop.lng}`
    : `https://www.openstreetmap.org/?mlat=${stop.lat}&mlon=${stop.lng}#map=17/${stop.lat}/${stop.lng}`;

  return (
    <div className="service-map-detail service-map-detail--real">
      <div className="service-map-detail__header">
        <div>
          <strong>Route {(row.routes || [row.route]).join(" / ")} live map</strong>
          <span>{row.destination} · {row.stop.name}</span>
        </div>
        <button type="button" onClick={onClose}>Close map</button>
      </div>

      <div className="service-map-detail__body service-map-detail__body--real">
        <MapTilePanel row={row} detailed />
        <div className="service-map-facts">
          <div><span>Due</span><strong>{due?.label || "—"}</strong><small>{due?.source === "expected" ? "Expected from live + timetable" : due?.detail || "No confirmed time"}</small></div>
          <div><span>Live tracking</span><strong>{estimate?.vehicleLat ? (safeExpected ? "Expected" : "Seen") : "None"}</strong><small>{estimate?.distance || "No matching bus position"}</small></div>
          <div><span>Map detail</span><strong>Road map</strong><small>Shows the bus, stop and nearby stops on OpenStreetMap tiles.</small></div>
          <a href={osmUrl} target="_blank" rel="noreferrer">Open in OpenStreetMap</a>
        </div>
      </div>
    </div>
  );
}

export default function LocalAreaBoard({ anchorStop, stops = [], vehicles = [], onBack }) {
  const [radiusKm, setRadiusKm] = useState(0.4);
  const [selectedStopOnly, setSelectedStopOnly] = useState(true);
  const [sameSideOnly, setSameSideOnly] = useState(true);
  const [showSchoolServices, setShowSchoolServices] = useState(false);
  const [showNoTimeRoutes, setShowNoTimeRoutes] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedMapKey, setExpandedMapKey] = useState(null);
  const [boardStyle, setBoardStyle] = useState("simple");
  const [fullPageBoard, setFullPageBoard] = useState(false);

  const localStops = useMemo(() => {
    if (!anchorStop?.lat || !anchorStop?.lng) return anchorStop ? [anchorStop] : [];
    if (selectedStopOnly) return [{ ...anchorStop, distanceKm: 0 }];
    const maxStops = MAX_STOPS_BY_RADIUS[radiusKm] || 14;
    const nearby = getNearbyStops(anchorStop.lat, anchorStop.lng, radiusKm, stops)
      .filter((stop) => stop.id)
      .filter((stop) => !sameSideOnly || isSameSide(anchorStop, stop))
      .slice(0, maxStops);
    const hasAnchor = nearby.some((stop) => stop.id === anchorStop.id);
    return hasAnchor ? nearby : [{ ...anchorStop, distanceKm: 0 }, ...nearby].slice(0, maxStops);
  }, [anchorStop, radiusKm, stops, sameSideOnly, selectedStopOnly]);

  useEffect(() => {
    let cancelled = false;
    if (!localStops.length) return undefined;
    setLoading(true);
    setRows([]);
    setErrors([]);
    setProgress({ done: 0, total: localStops.length });

    async function run() {
      const nextRows = [];
      const nextErrors = [];
      for (let index = 0; index < localStops.length; index += 1) {
        const stop = localStops[index];
        try {
          const data = await fetchStopServices(stop.id);
          const services = Array.isArray(data.services) ? data.services : [];
          nextRows.push(...services.map((service) => serviceToRow(service, stop)));
        } catch (error) {
          nextErrors.push(`${stop.name}: ${error.message}`);
        }
        if (!cancelled) {
          setRows([...nextRows]);
          setErrors([...nextErrors]);
          setProgress({ done: index + 1, total: localStops.length });
        }
      }
      if (!cancelled) setLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, [localStops, refreshKey]);

  const visibleRows = useMemo(() => {
    const withLive = rows.map((row) => addRowLive(row, vehicles));
    let filtered = showSchoolServices ? withLive : withLive.filter((row) => row.serviceType !== "School");
    if (!showNoTimeRoutes) filtered = filtered.filter((row) => row.nextTimes?.[0]);
    const grouped = groupBoardRows(filtered, selectedStopOnly);
    return grouped.sort((a, b) => {
      const aMins = a.nextTimes?.[0]?.minutes ?? 999999;
      const bMins = b.nextTimes?.[0]?.minutes ?? 999999;
      if (aMins !== bMins) return aMins - bMins;
      const routeCompare = String((a.routes || [a.route])[0]).localeCompare(String((b.routes || [b.route])[0]), undefined, { numeric: true });
      if (routeCompare !== 0) return routeCompare;
      return (a.stop.distanceKm ?? 0) - (b.stop.distanceKm ?? 0);
    });
  }, [rows, showSchoolServices, showNoTimeRoutes, vehicles, selectedStopOnly]);

  const schoolCount = rows.filter((row) => row.serviceType === "School").length;
  const timedRowsCount = rows.filter((row) => row.nextTimes?.[0]).length;
  const trackedCount = visibleRows.filter((row) => row.liveRouteCount > 0).length;
  const routeCount = new Set(visibleRows.flatMap((row) => row.routes || [row.route])).size;
  const stopCountWithServices = new Set(visibleRows.flatMap((row) => (row.stops || [row.stop]).map((stop) => stop.id))).size;
  const nextDue = visibleRows.find((row) => row.nextTimes?.[0]);

  useEffect(() => {
    document.body.classList.toggle("board-fullscreen-active", fullPageBoard);
    return () => document.body.classList.remove("board-fullscreen-active");
  }, [fullPageBoard]);

  if (!anchorStop) return null;

  if (fullPageBoard) {
    return (
      <div className="board-fullscreen-shell">
        <div className="board-fullscreen-actions">
          <button type="button" onClick={() => setFullPageBoard(false)}>Exit full page</button>
          <button type="button" onClick={() => setRefreshKey((value) => value + 1)}>Refresh</button>
        </div>
        <SimpleTvBoard rows={visibleRows} selectedStopOnly={selectedStopOnly} loading={loading} progress={progress} fullScreen />
      </div>
    );
  }

  return (
    <div className="area-board-shell">
      <button onClick={onBack} className="back-btn">← Back to stops</button>

      <section className="station-board-hero">
        <div>
          <div className="station-kicker">Bus departure board</div>
          <h2>{anchorStop.name}</h2>
          <p>
            Simple station-style view. It now shows only the next bus for each route/destination at the exact stop, with later times tucked underneath.
          </p>
        </div>
        <div className="station-stats">
          <div><strong>{localStops.length}</strong><span>{selectedStopOnly ? "stop checked" : "stops checked"}</span></div>
          <div><strong>{routeCount}</strong><span>routes shown</span></div>
          <div><strong>{nextDue?.nextTimes?.[0] ? formatNextLabel(nextDue.nextTimes[0]) : "—"}</strong><span>next bus</span></div>
        </div>
      </section>

      <section className="board-explainer-grid board-explainer-grid--simple">
        <div className="board-explainer-card"><strong>Due = GTFS timetable</strong><span>The main time comes from GTFS stop_times for the exact stop. Live tracking is shown only as map context.</span></div>
        <div className="board-explainer-card"><strong>One row per route</strong><span>Each route/destination is shown once, using the next departure as the main Due time. Later times sit underneath.</span></div>
      </section>

      <section className="area-board-controls">
        <div className="radius-control-block">
          <span className="control-label">Board view</span>
          <div className="radius-pills" aria-label="Board view mode">
            <button className={`radius-pill ${selectedStopOnly ? "active" : ""}`} onClick={() => setSelectedStopOnly(true)} type="button">Selected stop</button>
            <button className={`radius-pill ${!selectedStopOnly ? "active" : ""}`} onClick={() => setSelectedStopOnly(false)} type="button">Local area</button>
          </div>
        </div>

        <div className="radius-control-block">
          <span className="control-label">Board style</span>
          <div className="radius-pills" aria-label="Board style">
            <button className={`radius-pill ${boardStyle === "simple" ? "active" : ""}`} onClick={() => setBoardStyle("simple")} type="button">Simple board</button>
            <button className={`radius-pill ${boardStyle === "detailed" ? "active" : ""}`} onClick={() => setBoardStyle("detailed")} type="button">Detailed + map</button>
          </div>
        </div>

        <div className="radius-control-block">
          <span className="control-label">Display</span>
          <div className="radius-pills" aria-label="Display mode">
            <button className="radius-pill board-full-button" onClick={() => setFullPageBoard(true)} type="button">Open fullscreen board</button>
          </div>
        </div>

        {!selectedStopOnly && (
          <div className="radius-control-block">
            <span className="control-label">Bus stop radius</span>
            <div className="radius-pills" aria-label="Local area radius">
              {RADIUS_OPTIONS.map((value) => (
                <button key={value} className={`radius-pill ${radiusKm === value ? "active" : ""}`} onClick={() => setRadiusKm(value)} type="button">
                  {value < 1 ? `${Math.round(value * 1000)}m` : `${value.toFixed(1)}km`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="area-control-actions">
          {!selectedStopOnly && (
            <button className={`school-toggle ${sameSideOnly ? "on" : ""}`} onClick={() => setSameSideOnly((value) => !value)} type="button">
              {sameSideOnly ? "Same direction only" : "All directions"}
            </button>
          )}
          {schoolCount > 0 && <button className={`school-toggle ${showSchoolServices ? "on" : ""}`} onClick={() => setShowSchoolServices((value) => !value)} type="button">School: {showSchoolServices ? "On" : "Off"}</button>}
          <button className={`school-toggle ${showNoTimeRoutes ? "on" : ""}`} onClick={() => setShowNoTimeRoutes((value) => !value)} type="button">{showNoTimeRoutes ? "Hide no-time routes" : "Show no-time routes"}</button>
          <button className="refresh-board-btn" onClick={() => setRefreshKey((value) => value + 1)} type="button">↻ Refresh</button>
        </div>
      </section>

      <section className="local-board-summary">
        <div><span>Stops included</span><strong>{stopCountWithServices}</strong></div>
        <div><span>Timed services</span><strong>{timedRowsCount}</strong></div>
        <div><span>Live tracked rows</span><strong>{trackedCount}</strong></div>
      </section>

      {!selectedStopOnly && (
        <section className="nearby-stops-strip" aria-label="Nearby stops included">
          {localStops.map((stop) => <div key={stop.id} className={`nearby-stop-chip ${stop.id === anchorStop.id ? "selected" : ""}`}><strong>{stop.name}</strong><span>{formatDistance(stop)}</span></div>)}
        </section>
      )}

      {boardStyle === "simple" ? (
        <>
          <div className="simple-board-actions"><button className="fullscreen-hero-btn" onClick={() => setFullPageBoard(true)} type="button">⛶ Fullscreen departure board</button></div>
          <SimpleTvBoard rows={visibleRows} selectedStopOnly={selectedStopOnly} loading={loading} progress={progress} />
        </>
      ) : (
      <section className="train-board-card">
        <div className="train-board-topline">
          <div><span className="board-dot" /> Bus departures</div>
          <small>{loading ? `Checking ${progress.done}/${progress.total} ${selectedStopOnly ? "stop" : "stops"}…` : `Updated ${new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}</small>
        </div>

        <div className="train-board-head train-board-head--maps">
          <span>Service</span><span>Destination</span><span>{selectedStopOnly ? "Stop" : "Nearby stops"}</span><span>Due</span><span>Live bus</span>
        </div>

        {loading && !visibleRows.length && <div className="board-empty dark">Building departure board…<br />Checking exact timetable services.</div>}
        {!loading && !visibleRows.length && <div className="board-empty dark">No GTFS timed departures found for this stop. Check that data/gtfs.zip or BODS_GTFS_URL is configured, or try another stop.</div>}

        {visibleRows.map((row) => {
          const key = `${row.key}-${(row.routes || []).join("-")}-${(row.stops || []).map((s) => s.id).join("-")}`;
          const expanded = expandedMapKey === key;
          return (
            <div key={key} className="train-board-item">
              <div className="train-board-row train-board-row--maps">
                <RouteBadge route={row.route} routes={row.routes} />
                <div className="train-destination-cell"><strong>{row.destination}</strong><small>{row.operator}</small></div>
                <StopCell row={row} selectedStopOnly={selectedStopOnly} />
                <div className="train-time-cell"><BoardTimeCell row={row} /><div className="train-row-badges"><TypePill type={row.serviceType} /><span className="verified-label">Verified</span></div></div>
                <MiniBusMap row={row} onOpen={() => setExpandedMapKey(expanded ? null : key)} />
              </div>
              {expanded && <DetailedServiceMap row={row} onClose={() => setExpandedMapKey(null)} />}
            </div>
          );
        })}
      </section>
      )}

      {errors.length > 0 && <div className="info-box info-box--amber">Some stops could not be checked yet. {errors.slice(0, 2).join(" · ")}</div>}
      <p className="data-credit">GTFS mode: due times come from stop_times.txt for the exact stop. Live BODS vehicles are shown as map context only, not as the main timetable source.</p>
    </div>
  );
}
